import { closeLinkContextMenu } from '../../components/LinkContextMenu';
import ComposeLinkViewState from '../../store/schema/ComposeLinkViewState';
import { logUsage } from 'owa-analytics';
import { getLinkPermissionLevelString, ONE_DRIVE_PRO } from 'owa-attachment-permission';
import { updateSharePointLinkUrl } from 'owa-link-data';
import { lazyOnLinkHasChanged } from 'owa-recipient-permission-checker';
import AttachmentPermissionLevel from 'owa-service/lib/contract/AttachmentPermissionLevel';
import { SharingTipRecipientInfo } from 'owa-sharing-data';
import {
    createSharingLink,
    getSharingLink,
    SharingLinkInfo,
    updateSharePointLinkPermission,
} from 'owa-link-data';
import {
    IContextualMenuItem,
    IContextualMenuProps,
} from 'office-ui-fabric-react/lib/ContextualMenu';

const styles = require('../../components/LinkContextMenu.scss');

export function getChangePermissionContextMenuItem(
    composeLinkViewState: ComposeLinkViewState,
    composeId: string,
    recipientInfos: SharingTipRecipientInfo[]
): IContextualMenuItem | null {
    const sharingLinkInfo = getSharingLink(composeLinkViewState.linkId);
    return {
        text: getLinkPermissionLevelString(sharingLinkInfo.permissionLevel, ONE_DRIVE_PRO),
        key: 'changePermission',
        className: styles.smallFontContextMenu,
        subMenuProps: getSupportedPermissionLevelsSubMenuItems(
            sharingLinkInfo,
            composeId,
            recipientInfos
        ),
    };
}

export function getSupportedPermissionLevelsSubMenuItems(
    sharingLinkInfo: SharingLinkInfo,
    composeId: string,
    recipientInfos: SharingTipRecipientInfo[]
): IContextualMenuProps {
    const subMenuItems: IContextualMenuItem[] = [];

    if (sharingLinkInfo.supportedPermissionLevels) {
        sharingLinkInfo.supportedPermissionLevels.forEach(permissionLevel => {
            if (
                permissionLevel !== AttachmentPermissionLevel.View &&
                permissionLevel !== AttachmentPermissionLevel.Edit
            ) {
                const permission = getLinkPermissionLevelString(permissionLevel, ONE_DRIVE_PRO);
                subMenuItems.push({
                    text: permission,
                    key: permission,
                    title: permission,
                    onClick: () => {
                        logUsage('AttachmentLinkViewChangePermission');
                        setSharingLinkPermission(
                            permissionLevel,
                            sharingLinkInfo,
                            composeId,
                            recipientInfos
                        );
                        closeLinkContextMenu();
                    },
                    isChecked: permissionLevel === sharingLinkInfo.permissionLevel,
                    canCheck: true,
                });
            }
        });
    }
    if (subMenuItems.length > 0) {
        return {
            items: subMenuItems,
            className: styles.fixWidthContextMenu,
            directionalHintFixed: true,
        };
    } else {
        return null;
    }
}

function setSharingLinkPermission(
    newPermissionLevel: AttachmentPermissionLevel,
    sharingLinkInfo: SharingLinkInfo,
    composeId: string,
    recipientInfos: SharingTipRecipientInfo[]
) {
    if (newPermissionLevel === sharingLinkInfo.permissionLevel) {
        return;
    }

    const sharingUrl = getSharingUrlsForPermissionLevel(newPermissionLevel, sharingLinkInfo);

    updateSharePointLinkPermission(sharingLinkInfo.linkId, newPermissionLevel);
    if (sharingUrl) {
        updateSharePointLinkUrl(
            sharingLinkInfo.linkId,
            sharingUrl,
            sharingLinkInfo.url,
            null /* expirationDate. Will always be null, as if the link expires we do not cache it */
        );
    } else {
        //TODO: VSO 36802 - add error checking code
        createSharingLink(
            sharingLinkInfo.linkId,
            sharingLinkInfo.url,
            sharingLinkInfo.sharingInfo.canonicalUrl,
            newPermissionLevel
        );
    }

    lazyOnLinkHasChanged.importAndExecute(
        sharingLinkInfo.linkId,
        newPermissionLevel,
        recipientInfos,
        composeId
    );
    return;
}

function getSharingUrlsForPermissionLevel(
    newPermissionLevel: AttachmentPermissionLevel,
    sharingLinkInfo: SharingLinkInfo
): string | null {
    // For anonymous links we should aways return null, as the current url could be expired
    switch (newPermissionLevel) {
        case AttachmentPermissionLevel.OrganizationEdit:
            return sharingLinkInfo.sharingUrls.organizationEditLink;
        case AttachmentPermissionLevel.OrganizationView:
            return sharingLinkInfo.sharingUrls.organizationViewLink;
        case AttachmentPermissionLevel.Edit:
        case AttachmentPermissionLevel.View:
        case AttachmentPermissionLevel.None:
            return sharingLinkInfo.sharingInfo.canonicalUrl;
        default:
            return null;
    }
}
